/**
 * AnalyticsPage Component
 *
 * Dashboard for gym owners to view analytics data including:
 * - Most logged routes
 * - Highest/lowest rated routes
 * - Grade distribution
 * - Setter performance
 *
 * Only accessible to gym owners.
 */

import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { useGym } from '@/hooks/useGyms';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { MostLoggedRoutes } from '@/components/analytics/MostLoggedRoutes';
import { RouteRatingsChart } from '@/components/analytics/RouteRatingsChart';
import { GradeDistributionChart } from '@/components/analytics/GradeDistributionChart';
import { SetterPerformanceTable } from '@/components/analytics/SetterPerformanceTable';
import type { VGrade } from '@flash/shared';

/**
 * Route with stats for analytics display
 */
interface RouteWithStats {
  id: string;
  name: string;
  officialGrade: VGrade;
  totalSends: number;
  totalAttempts: number;
  averageRating: number | null;
  ratingCount: number;
  setterName: string;
}

/**
 * Setter performance metrics
 */
interface SetterStats {
  setterId: string;
  setterName: string;
  routeCount: number;
  averageRating: number | null;
  totalSends: number;
}

/**
 * Response type from getGymAnalytics callable function
 */
interface GymAnalyticsResponse {
  mostLoggedRoutes: RouteWithStats[];
  highestRatedRoutes: RouteWithStats[];
  lowestRatedRoutes: RouteWithStats[];
  gradeDistribution: Record<VGrade, number>;
  setterPerformance: SetterStats[];
  totalActiveRoutes: number;
  totalRetiredRoutes: number;
}

/**
 * AnalyticsPage displays gym analytics for gym owners
 *
 * @example
 * ```tsx
 * // In router
 * <Route path="/gyms/:gymId/analytics" element={<AnalyticsPage />} />
 * ```
 */
export const AnalyticsPage = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const _auth = useAuth();
  const { gym, loading: gymLoading, error: gymError, refetch: refetchGym } = useGym(gymId);
  const { getRoleAtGym, loading: membershipLoading } = useGymMemberships();

  // Analytics data state
  const [analytics, setAnalytics] = useState<GymAnalyticsResponse | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const loading = gymLoading || membershipLoading;
  const userRole = gymId ? getRoleAtGym(gymId) : null;
  const isOwner = userRole === 'owner';

  /**
   * Fetch analytics data from the callable function
   */
  const fetchAnalytics = useCallback(async () => {
    if (!gymId || !isOwner) return;

    setAnalyticsLoading(true);
    setAnalyticsError(null);

    try {
      const functions = getFirebaseFunctions();
      const getGymAnalytics = httpsCallable<{ gymId: string }, GymAnalyticsResponse>(
        functions,
        'getGymAnalytics'
      );

      const result = await getGymAnalytics({ gymId });
      setAnalytics(result.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load analytics';
      setAnalyticsError(errorMessage);
      console.error('Error fetching analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [gymId, isOwner]);

  // Fetch analytics when gym is loaded and user is owner
  useEffect(() => {
    if (!loading && isOwner) {
      fetchAnalytics();
    }
  }, [loading, isOwner, fetchAnalytics]);

  // Loading state
  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading analytics...</p>
        </div>
      </main>
    );
  }

  // Error state
  if (gymError) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h2 style={errorTitleStyle}>Error Loading Gym</h2>
          <p style={errorTextStyle}>{gymError}</p>
          <button onClick={() => refetchGym()} style={retryButtonStyle}>
            Try Again
          </button>
        </div>
      </main>
    );
  }

  // Not found state
  if (!gym) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h2 style={errorTitleStyle}>Gym Not Found</h2>
          <p style={errorTextStyle}>{"The gym you're looking for doesn't exist."}</p>
        </div>
      </main>
    );
  }

  // Not owner - access denied
  if (!isOwner) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h2 style={errorTitleStyle}>Access Denied</h2>
          <p style={errorTextStyle}>Only the gym owner can view analytics.</p>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <div style={headerContentStyle}>
          <h1 style={pageTitleStyle}>Gym Analytics</h1>
          <p style={gymNameStyle}>{gym.name}</p>
        </div>
        <button
          onClick={fetchAnalytics}
          disabled={analyticsLoading}
          style={{
            ...refreshButtonStyle,
            ...(analyticsLoading ? disabledButtonStyle : {}),
          }}
        >
          {analyticsLoading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </header>

      {/* Analytics Error */}
      {analyticsError && (
        <div style={analyticsErrorStyle}>
          <p>{analyticsError}</p>
          <button onClick={fetchAnalytics} style={retrySmallButtonStyle}>
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {analyticsLoading && !analytics && (
        <div style={loadingAnalyticsStyle}>
          <div style={skeletonCardStyle}>
            <div style={skeletonHeaderStyle} />
            <div style={skeletonBodyStyle} />
          </div>
          <div style={skeletonCardStyle}>
            <div style={skeletonHeaderStyle} />
            <div style={skeletonBodyStyle} />
          </div>
        </div>
      )}

      {/* Analytics Content */}
      {analytics && (
        <div style={contentStyle}>
          {/* Summary Stats */}
          <section style={summaryStyle}>
            <div style={summaryCardStyle}>
              <span style={summaryValueStyle}>{analytics.totalActiveRoutes}</span>
              <span style={summaryLabelStyle}>Active Routes</span>
            </div>
            <div style={summaryCardStyle}>
              <span style={summaryValueStyle}>{analytics.totalRetiredRoutes}</span>
              <span style={summaryLabelStyle}>Retired Routes</span>
            </div>
            <div style={summaryCardStyle}>
              <span style={summaryValueStyle}>{analytics.setterPerformance.length}</span>
              <span style={summaryLabelStyle}>Active Setters</span>
            </div>
          </section>

          {/* Most Logged Routes */}
          <section style={sectionStyle}>
            <MostLoggedRoutes routes={analytics.mostLoggedRoutes} />
          </section>

          {/* Route Ratings */}
          <section style={sectionStyle}>
            <RouteRatingsChart
              highestRated={analytics.highestRatedRoutes}
              lowestRated={analytics.lowestRatedRoutes}
            />
          </section>

          {/* Grade Distribution */}
          <section style={sectionStyle}>
            <GradeDistributionChart distribution={analytics.gradeDistribution} />
          </section>

          {/* Setter Performance */}
          <section style={sectionStyle}>
            <SetterPerformanceTable setters={analytics.setterPerformance} />
          </section>
        </div>
      )}
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f9fafb',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: '#6b7280',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
  padding: '20px',
  textAlign: 'center',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  marginBottom: '16px',
};

const retryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
  marginBottom: '12px',
};

const headerStyle: CSSProperties = {
  backgroundColor: 'white',
  borderBottom: '1px solid #e5e7eb',
  padding: '20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '16px',
};

const headerContentStyle: CSSProperties = {
  maxWidth: '800px',
};

const pageTitleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1f2937',
  margin: '12px 0 4px',
};

const gymNameStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  margin: 0,
};

const refreshButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: '#9ca3af',
  cursor: 'not-allowed',
};

const analyticsErrorStyle: CSSProperties = {
  margin: '20px',
  padding: '16px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  color: '#991b1b',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
};

const retrySmallButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: '#991b1b',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const loadingAnalyticsStyle: CSSProperties = {
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  maxWidth: '1000px',
  margin: '0 auto',
};

const skeletonCardStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '24px',
};

const skeletonHeaderStyle: CSSProperties = {
  height: '24px',
  width: '200px',
  backgroundColor: '#e5e7eb',
  borderRadius: '4px',
  marginBottom: '16px',
  animation: 'pulse 1.5s ease-in-out infinite',
};

const skeletonBodyStyle: CSSProperties = {
  height: '120px',
  backgroundColor: '#f3f4f6',
  borderRadius: '8px',
  animation: 'pulse 1.5s ease-in-out infinite',
};

const contentStyle: CSSProperties = {
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

const summaryStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '16px',
};

const summaryCardStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

const summaryValueStyle: CSSProperties = {
  fontSize: '2rem',
  fontWeight: 700,
  color: '#1f2937',
};

const summaryLabelStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#6b7280',
  marginTop: '4px',
};

const sectionStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '24px',
};

export default AnalyticsPage;
